import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Icon } from "@dtw/icons";
import { getForecast, getVervolgacties, listLeads, updateContactmoment } from "@/features/leads/leads.api";
import { fmtMoney, fmtDate, fmtNumber } from "@/lib/format";
import type { LeadKwalificatie } from "@/lib/types";

// Leads en de terugbellijst. Niks fancy, praktisch: waar staat welke kans, wat is
// het verwachte bedrag per kwartaal, en wie moet er deze week gebeld worden.
export const Route = createFileRoute("/_shell/leads")({
  validateSearch: (search: Record<string, unknown>) => ({
    status: typeof search["status"] === "string" ? search["status"] : undefined,
    kwalificatie: typeof search["kwalificatie"] === "string" ? search["kwalificatie"] : undefined,
    q: typeof search["q"] === "string" ? search["q"] : undefined,
  }),
  component: LeadsPage,
});

const KWALIFICATIES: LeadKwalificatie[] = ["a", "b", "c"];
const PERIODES = ["week", "maand", "kwartaal"] as const;

function LeadsPage() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();
  const [periode, setPeriode] = useState<(typeof PERIODES)[number]>("week");

  const leadsQuery = useQuery({
    queryKey: ["leads", search],
    queryFn: () => listLeads({ ...search, pageSize: "100" }),
    placeholderData: keepPreviousData,
  });
  const forecastQuery = useQuery({ queryKey: ["leads", "forecast"], queryFn: () => getForecast() });
  const actiesQuery = useQuery({
    queryKey: ["vervolgacties", periode],
    queryFn: () => getVervolgacties(periode),
  });

  const afvinken = useMutation({
    mutationFn: (id: string) => updateContactmoment(id, { afgerond: true }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["vervolgacties"] });
      void queryClient.invalidateQueries({ queryKey: ["klant"] });
    },
  });

  const leads = leadsQuery.data?.data ?? [];
  const acties = actiesQuery.data?.acties ?? [];
  const setSearch = (patch: Record<string, string | undefined>) =>
    navigate({ search: (prev) => ({ ...prev, ...patch }) });

  return (
    <div className="app-page" data-screen-label="Backend Leads">
      <div className="page-header">
        <div>
          <div className="page-header__title">
            <h1>{t("leads.title")}</h1>
          </div>
          <p className="page-header__sub">{t("leads.subtitle")}</p>
        </div>
        <div className="page-header__actions">
          <button className="dtw-btn dtw-btn--accent">
            <Icon name="plus" size={14} /> {t("leads.new")}
          </button>
        </div>
      </div>

      {/* Verwachte omzet per kwartaal. Ongewogen — A/B/C is een kwalificatie en
          geen kans, dus de klassen staan náást elkaar in plaats van vermenigvuldigd. */}
      <div className="groep-strip">
        {(forecastQuery.data?.kwartalen ?? []).map((k) => (
          <div className="groep-card" key={`${k.jaar}-${k.kwartaal}`}>
            <span className="groep-card__name">
              {k.jaar} · K{k.kwartaal}
            </span>
            <span className="groep-card__count">
              {fmtMoney(k.maandbedragCents)} <span className="t-tertiary">{t("pakketten.perMaand")}</span>
            </span>
            <span className="groep-card__ledger">
              {t("common.results", { count: k.aantal })} ·{" "}
              {KWALIFICATIES.filter((q) => k.perKwalificatie[q]).map((q) => (
                <span key={q}>
                  {q.toUpperCase()} {fmtMoney(k.perKwalificatie[q]!.maandbedragCents)}{" "}
                </span>
              ))}
            </span>
          </div>
        ))}
        {(forecastQuery.data?.kwartalen ?? []).length === 0 && (
          <p className="t-tertiary" style={{ fontSize: "var(--fs-sm)" }}>
            {t("leads.geenForecast")}
          </p>
        )}
      </div>

      {/* Terugbellijst */}
      <div className="panel" style={{ marginBottom: "var(--space-4)" }}>
        <div className="panel__head">
          <div className="panel__title">{t("leads.terugbellijst")}</div>
          <span className="dtw-spacer" />
          <div className="views">
            {PERIODES.map((p) => (
              <button
                key={p}
                className={`view-chip${periode === p ? " is-active" : ""}`}
                onClick={() => setPeriode(p)}
              >
                {t(`leads.periode.${p}` as never)}
              </button>
            ))}
          </div>
        </div>
        <div className="panel__body flush">
          {acties.length === 0 ? (
            <div className="empty">
              <h3>{t("leads.geenActies")}</h3>
              <p>{t("leads.geenActiesBody")}</p>
            </div>
          ) : (
            <div className="dtw-table-wrap">
              <table className="dtw-table">
                <thead>
                  <tr>
                    <th>{t("leads.col.terugbellenOp")}</th>
                    <th>{t("klanten.col.klant")}</th>
                    <th>{t("leads.col.actie")}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {acties.map((a) => (
                    <tr key={a.id}>
                      <td className="mono">
                        {a.achterstallig && (
                          <span className="t-danger" title={t("leads.achterstallig")}>
                            <Icon name="alert-triangle" size={12} />{" "}
                          </span>
                        )}
                        {fmtDate(a.vervolgOp)}
                      </td>
                      <td>
                        <Link to="/klanten/$id" params={{ id: a.klantId }}>
                          {a.klantNaam}
                        </Link>
                      </td>
                      <td>{a.tekst}</td>
                      <td className="num">
                        <button
                          className="dtw-btn dtw-btn--secondary dtw-btn--sm"
                          disabled={afvinken.isPending}
                          onClick={() => afvinken.mutate(a.id)}
                        >
                          <Icon name="check" size={13} /> {t("leads.afvinken")}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Leads */}
      <div className="panel">
        <div className="panel__head">
          <div className="views">
            <button
              className={`view-chip${!search.status ? " is-active" : ""}`}
              onClick={() => setSearch({ status: undefined })}
            >
              {t("pakketten.filter.alle")}
            </button>
            {(["open", "gewonnen", "verloren"] as const).map((s) => (
              <button
                key={s}
                className={`view-chip${search.status === s ? " is-active" : ""}`}
                onClick={() => setSearch({ status: s })}
              >
                {t(`leads.status.${s}` as never)}
              </button>
            ))}
            <span className="dtw-divider--vertical" />
            {KWALIFICATIES.map((q) => (
              <button
                key={q}
                className={`view-chip${search.kwalificatie === q ? " is-active" : ""}`}
                onClick={() => setSearch({ kwalificatie: search.kwalificatie === q ? undefined : q })}
              >
                {q.toUpperCase()}
              </button>
            ))}
          </div>
          <span className="dtw-spacer" />
          <span className="meta">{t("common.results", { count: leadsQuery.data?.total ?? 0 })}</span>
        </div>
        <div className="panel__body flush">
          {leads.length === 0 ? (
            <div className="empty">
              <h3>{t("leads.empty.title")}</h3>
              <p>{t("leads.empty.body")}</p>
            </div>
          ) : (
            <div className="dtw-table-wrap">
              <table className="dtw-table">
                <thead>
                  <tr>
                    <th>{t("leads.col.kwalificatie")}</th>
                    <th>{t("klanten.col.klant")}</th>
                    <th>{t("leads.col.omschrijving")}</th>
                    <th>{t("leads.col.verwachtePeriode")}</th>
                    <th className="num">{t("leads.col.verwachtBedrag")}</th>
                    <th>{t("leads.col.status")}</th>
                    <th className="num">{t("leads.col.notities")}</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((l) => (
                    <tr key={l.id} className={l.status !== "open" ? "is-muted" : ""}>
                      <td>
                        <span className={`dtw-chip lead-kwal lead-kwal--${l.kwalificatie}`}>
                          {l.kwalificatieLabel}
                        </span>
                      </td>
                      <td>
                        <Link to="/klanten/$id" params={{ id: l.klant.id }}>
                          {l.klant.naam}
                        </Link>
                      </td>
                      <td>{l.omschrijving}</td>
                      <td className="mono">
                        {l.verwachtJaar}-{String(l.verwachtMaand).padStart(2, "0")}
                        <span className="t-tertiary"> · K{l.verwachtKwartaal}</span>
                      </td>
                      <td className="num mono">
                        {fmtMoney(l.verwachtMaandbedragCents)}
                        <span className="t-tertiary"> /mnd</span>
                      </td>
                      <td>
                        <span
                          className={`dtw-chip${
                            l.status === "gewonnen" ? " dtw-chip--eco" : l.status === "open" ? " dtw-chip--indigo" : ""
                          }`}
                        >
                          {l.statusLabel}
                        </span>
                      </td>
                      <td className="num mono">{fmtNumber(l.aantalContactmomenten)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="panel__foot">
          <span className="t-tertiary">{t("leads.forecastNote")}</span>
        </div>
      </div>
    </div>
  );
}
